import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Resets window scroll to the top whenever the route pathname changes, so a
 * user who clicks a footer link lands at the top of the next page.
 * Back/forward (POP) keeps the browser's restored position, and hash links
 * (`/page#section`) are left to scroll to their anchor.
 *
 * Only a real pathname change counts. Query-only navigations — e.g. opening
 * the product modal (`?product=…`, a REPLACE) — must not scroll: right after a
 * page load the navigation type is POP, so that REPLACE changed
 * `navigationType` alone, re-ran this effect and jumped the page to the top
 * behind the modal.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const previousPathnameRef = useRef(pathname);

  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;
    if (previousPathname === pathname) return;
    // /products/:id opens the product modal over the current page: not a page
    // change (same rule as the route loader in routes/index.jsx).
    if (previousPathname?.startsWith('/products/') || pathname.startsWith('/products/')) return;
    if (navigationType === 'POP' || hash) return;
    // The reset gives html/body/#root `height: 100%` + `overflow-x: hidden`,
    // which makes #root (or body) the real scroll container, not the window.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    [document.documentElement, document.body, document.getElementById('root')].forEach((el) => {
      if (el) el.scrollTop = 0;
    });
  }, [pathname, hash, navigationType]);

  return null;
}
